// tslint:disable:deprecation

import * as React from 'react';
import { TabStyles } from '../styles/TabStyles';
import { GlobalPointerDownOutsideListener } from '../utils/GlobalPointerDownOutsideListener';
import { HorizontalOriginOption, Overlay, OverlayProps, VerticalOriginOption } from '../utils/Overlay';
import { GenericListMenuProps, ListMenuWidget } from './ListMenuWidget';

export interface BaseFlyoutListMenuProps extends Omit<OverlayProps, 'autoFocus' | 'disablePointerEvents' | 'portalEventTrapOverrides'> {
  onMouseEnter?: React.MouseEventHandler<HTMLElement>;
  /**
   * The menu will not be re-triggered by clicking this element when the menu is already open.
   */
  toggleEl?: HTMLElement | null;
  /**
   * When true, focus will be returned to `toggleEl` when the menu closes.
   * @default ```true```
   */
  shouldReturnFocusToToggleEl?: boolean;
}

export interface GenericFlyoutListMenuProps extends BaseFlyoutListMenuProps, GenericListMenuProps {
}

const DefaultAnchorOrigin = {
  vertical: VerticalOriginOption.bottom,
  horizontal: HorizontalOriginOption.left
};

const { Sizing, Colors, Shadow } = TabStyles;

export const FlyoutListMenuStyles: Readonly<React.CSSProperties> = {
  backgroundColor: 'white',
  borderColor: Colors.L6,
  borderRadius: Sizing.BorderRadius,
  borderStyle: 'solid',
  borderWidth: Sizing.BorderWidth,
  boxShadow: Shadow.Standard
};

export class GenericFlyoutListMenuWidget extends React.Component<GenericFlyoutListMenuProps> {
  private unsubscribeFlyoutFromGlobalListener: (() => void) | null = null;
  private unsubscribeToggleElFromGlobalListener: (() => void) | null = null;
  private flyoutDomNode: HTMLElement | null = null;

  public static defaultProps: Pick<GenericFlyoutListMenuProps, 'anchorOrigin' | 'role' | 'shouldReturnFocusToToggleEl'> = {
    anchorOrigin: DefaultAnchorOrigin,
    role: 'menu',
    shouldReturnFocusToToggleEl: true,
  };

  public componentDidMount(): void {
    // Everything outside the toggling element/button or the flyout should trigger a close on mousedown
    if (this.flyoutDomNode) {
      this.unsubscribeFlyoutFromGlobalListener = GlobalPointerDownOutsideListener.subscribe(
        this.flyoutDomNode,
        this.props.requestCloseAll
      );
    }
    if (this.props.toggleEl) {
      this.unsubscribeToggleElFromGlobalListener = GlobalPointerDownOutsideListener.subscribe(
        this.props.toggleEl,
        this.props.requestCloseAll
      );
    }
  }

  public componentWillUnmount(): void {
    this.unsubscribeFlyoutFromGlobalListener?.();
    this.unsubscribeToggleElFromGlobalListener?.();

    if (this.props.shouldReturnFocusToToggleEl) {
      this.props.toggleEl?.focus();
    }
  }

  private setFlyoutDomNode = (ref: HTMLDivElement | null): void => {
    this.flyoutDomNode = ref;
  }

  private getAnchorClientRect = (): ClientRect | undefined => {
    if (this.props.getAnchorClientRect) {
      return this.props.getAnchorClientRect();
    }

    return this.props.anchorEl ? this.props.anchorEl.getBoundingClientRect() : undefined;
  }

  private handleKeyDown: React.KeyboardEventHandler<HTMLElement> = e => {
    if (e.key === 'Tab') {
      e.stopPropagation();
      e.preventDefault();
    }
  }

  public render(): JSX.Element {
    // tslint:disable-next-line:max-line-length
    const { allowAutoPosition, anchorOrigin, handleScrollOnScrollingRoot, leftFromOverlaidPosition, menuStyle, onMouseEnter, scrollingRootId, shouldAutoPositionOnScrollingRootScroll, stackedOrigin, stackingRootId, topFromOverlaidPosition, zIndex, ...menuProps } = this.props;

    const overlayProps: OverlayProps = {
      allowAutoPosition,
      anchorOrigin,
      getAnchorClientRect: this.getAnchorClientRect,
      handleScrollOnScrollingRoot,
      leftFromOverlaidPosition,
      scrollingRootId,
      shouldAutoPositionOnScrollingRootScroll,
      stackedOrigin,
      stackingRootId,
      topFromOverlaidPosition,
      zIndex,
    };

    const containerProps: React.ClassAttributes<HTMLDivElement> & React.HTMLAttributes<HTMLDivElement> = {
      onKeyDown: this.handleKeyDown, // not using FocusTrap for this because it'll prevent submenu navigation
      onMouseEnter,
      ref: this.setFlyoutDomNode,
      style: { ...FlyoutListMenuStyles, ...menuStyle },
    };

    const listMenuProps: GenericListMenuProps = {
      ...menuProps,
      scrollingRootId: overlayProps.scrollingRootId,
      stackingRootId: overlayProps.stackingRootId,
      subMenuAllowAutoPosition: overlayProps.allowAutoPosition,
      subMenuShouldAutoPositionOnScrollingRootScroll: overlayProps.shouldAutoPositionOnScrollingRootScroll,
      zIndex: overlayProps.zIndex,
    };

    return (
      <Overlay {...overlayProps}>
        <div {...containerProps}>
          <ListMenuWidget {...listMenuProps} />
        </div>
      </Overlay>
    );
  }
}
